export type FileAcceptEntry = {
  kind: 'extension' | 'mime' | 'mimeWildcard';
  value: string;
};

export type FileAcceptFilterResult = {
  acceptedFiles: File[];
  rejectedFiles: File[];
  acceptedTypes: string[];
};

const extensionPattern = /^\.\S+$/;
const mimePattern = /^([a-z0-9!#$&^_.+-]+)\/([a-z0-9!#$&^_.+-]+|\*)$/i;

export function parseFileAccept(accept?: string): FileAcceptEntry[] {
  if (!accept) {
    return [];
  }

  const entries = new Map<string, FileAcceptEntry>();

  for (const rawEntry of accept.split(',')) {
    const value = rawEntry.trim().toLowerCase();

    if (!value || entries.has(value)) {
      continue;
    }

    if (extensionPattern.test(value)) {
      entries.set(value, { kind: 'extension', value });
      continue;
    }

    const mimeMatch = value.match(mimePattern);
    if (!mimeMatch) {
      continue;
    }

    entries.set(value, {
      kind: mimeMatch[2] === '*' ? 'mimeWildcard' : 'mime',
      value,
    });
  }

  return Array.from(entries.values());
}

export function filterFilesByAccept(files: File[], accept?: string): FileAcceptFilterResult {
  const acceptEntries = parseFileAccept(accept);

  if (!acceptEntries.length) {
    return {
      acceptedFiles: files,
      rejectedFiles: [],
      acceptedTypes: [],
    };
  }

  const acceptedFiles: File[] = [];
  const rejectedFiles: File[] = [];

  for (const file of files) {
    const normalizedName = file.name.toLowerCase();
    const normalizedMime = file.type.trim().toLowerCase();
    const isAccepted = acceptEntries.some((entry) => {
      if (entry.kind === 'extension') {
        return normalizedName.endsWith(entry.value);
      }

      if (!normalizedMime) {
        return false;
      }

      if (entry.kind === 'mime') {
        return normalizedMime === entry.value;
      }

      return normalizedMime.startsWith(entry.value.slice(0, -1));
    });

    (isAccepted ? acceptedFiles : rejectedFiles).push(file);
  }

  return {
    acceptedFiles,
    rejectedFiles,
    acceptedTypes: acceptEntries.map(({ value }) => value),
  };
}
